import { appState } from './state'
import { Card } from '../Models/Card'

/**
 * LAYOUTS
 * =======
 *
 * Portrait: Vertical Solitaire layout — uniform 9-row grid
 * --------------------------------------------------------
 * The entire screen is a single uniform grid. Every row is the same height
 * (cardHeight + cardSpacing). Buttons, cards, and placeholders all align
 * to this grid — no separate "button area".
 *
 * Grid assignment:
 *   Row 0:  Menu (side)              +  Timer (center)
 *   Row 1:  New Game (center)        +  Tableau[0] (main)
 *   Row 2:  Undo (side)             +  Tableau[1] (main)
 *   Row 3:  Autocomplete (side) / Foundation[0] (side) + Tableau[2] (main)
 *   Row 4:  Foundation[1] (side)     +  Tableau[3] (main)
 *   Row 5:  Foundation[2] (side)     +  Tableau[4] (main)
 *   Row 6:  Foundation[3] (side)     +  Tableau[5] (main)
 *   Row 7:  (empty)                  +  Tableau[6] (main)
 *   Row 8:  Stock (side)             +  Waste (main)
 *
 * Landscape: Classic layout
 * -------------------------
 * Top row: foundations on the far side, stock/waste on the thumb side,
 * with a centered gap for the timer. Tableau columns run left → right and
 * cascade downward.
 */

// ─── Helpers ────────────────────────────────────────────────────────────

export function isLeftHanded(): boolean {
    return appState.settings.handedness === 'left'
}

export function isLandscapeLayout(): boolean {
    return appState.windowWidth > appState.windowHeight
}

// ─── Uniform grid ───────────────────────────────────────────────────────

/** Y position for grid row i (0-indexed). All 9 rows use the same step. */
export function rowY(i: number): number {
    return Math.floor(appState.cardYOffset + (appState.cardHeight + appState.cardSpacing) * i)
}

/**
 * Y position for button row i (0-indexed).
 * Uses a tighter step (cardWidth + cardSpacing) so Menu, New Game, and Undo
 * cluster together without the large gaps of the full card-height grid.
 */
function buttonRowY(i: number): number {
    const baseY = isLandscapeLayout() ? rowY(1) : appState.cardYOffset
    return Math.floor(baseY + (appState.cardWidth + appState.cardSpacing) * i)
}

// ─── Landscape helpers ───────────────────────────────────────────────────

function landscapeTopRowY(): number {
    return rowY(0)
}

function landscapeTableauRowY(): number {
    return rowY(2)
}

// Memoization for landscapeClusterMetrics — the inputs that affect the result
interface LandscapeClusterResult {
    gap: number
    stockOnLeft: boolean
    stockX: number
    wasteX: number
    foundationClusterX: number
}
let _lscCacheKey = ''
let _lscCachedResult: LandscapeClusterResult | null = null

function landscapeClusterMetrics() {
    // Build a cache key from all inputs that affect the result
    const key = `${appState.cardWidth}|${appState.cardSpacing}|${appState.windowWidth}|${isLeftHanded()}`
    if (_lscCachedResult && key === _lscCacheKey) {
        return _lscCachedResult
    }

    const cw = appState.cardWidth
    const spacing = appState.cardSpacing
    const gap = Math.max(spacing, Math.floor(cw * 0.2))
    const middleGap = Math.max(cw, Math.floor(cw * 1.1))

    const foundationWidth = cw * appState.NUM_FOUNDATIONS + gap * (appState.NUM_FOUNDATIONS - 1)
    const stockWidth = cw * 2 + gap

    const stockOnLeft = isLeftHanded()
    const centerX = appState.windowWidth / 2
    const leftWidth = stockOnLeft ? stockWidth : foundationWidth
    const rightWidth = stockOnLeft ? foundationWidth : stockWidth

    let leftX = Math.floor(centerX - middleGap / 2 - leftWidth)
    let rightX = Math.floor(centerX + middleGap / 2)

    const padding = Math.max(4, Math.floor(cw * 0.1))
    const minX = Math.min(leftX, rightX)
    const maxX = Math.max(leftX + leftWidth, rightX + rightWidth)
    let shift = 0
    if (minX < padding) {
        shift = padding - minX
    }
    if (maxX + shift > appState.windowWidth - padding) {
        shift -= maxX + shift - (appState.windowWidth - padding)
    }

    leftX += shift
    rightX += shift

    const stockClusterX = stockOnLeft ? leftX : rightX
    const foundationClusterX = stockOnLeft ? rightX : leftX

    const stockX = stockOnLeft ? stockClusterX : stockClusterX + cw + gap
    const wasteX = stockOnLeft ? stockClusterX + cw + gap : stockClusterX

    const result = {
        gap,
        stockOnLeft,
        stockX,
        wasteX,
        foundationClusterX,
    }

    _lscCacheKey = key
    _lscCachedResult = result
    return result
}

function landscapeTableauMetrics() {
    const cw = appState.cardWidth
    const spacing = appState.cardSpacing
    const sidePadding = Math.max(spacing, Math.floor(cw * 0.35))
    const availableWidth = appState.windowWidth - sidePadding * 2
    const rawGap = Math.floor((availableWidth - appState.NUM_TABLEAU * cw) / (appState.NUM_TABLEAU - 1))
    const gap = Math.max(2, rawGap)
    const totalWidth = appState.NUM_TABLEAU * cw + (appState.NUM_TABLEAU - 1) * gap
    const startX = Math.floor((appState.windowWidth - totalWidth) / 2)

    return { startX, gap }
}

// ─── X columns ──────────────────────────────────────────────────────────

/** X for the side column (thumb side): menu, undo, autocomplete, foundations, stock */
function sideColumnX(): number {
    if (isLandscapeLayout()) {
        const { stockX } = landscapeClusterMetrics()
        return stockX
    }
    if (isLeftHanded()) {
        return Math.floor(appState.cardWidth * 0.35)
    }
    return Math.floor(appState.windowWidth - appState.cardWidth * 1.35)
}

/** X for the main column: tableau base cards, waste */
function mainColumnX(): number {
    if (isLandscapeLayout()) {
        const { wasteX } = landscapeClusterMetrics()
        return wasteX
    }
    if (isLeftHanded()) {
        return Math.floor(appState.cardWidth * 1.7)
    }
    return Math.floor(appState.windowWidth - appState.cardWidth * 2.7)
}

/** X for the opposite column (far side): new game only */
export function oppositeColumnX(): number {
    if (isLandscapeLayout()) {
        return Math.floor(appState.windowWidth - sideColumnX() - appState.cardWidth)
    }
    if (isLeftHanded()) {
        return Math.floor(appState.windowWidth - appState.cardWidth * 1.35)
    }
    return Math.floor(appState.cardWidth * 0.35)
}

/** X to center a card-width element horizontally on screen */
function centerColumnX(): number {
    return Math.floor((appState.windowWidth - appState.cardWidth) / 2)
}

// ─── Stock (Row 8) ──────────────────────────────────────────────────────

export function stockCardX(): number {
    if (isLandscapeLayout()) {
        return landscapeClusterMetrics().stockX
    }
    return sideColumnX()
}
export function stockCardY(): number {
    return isLandscapeLayout() ? landscapeTopRowY() : rowY(8)
}
export function stockPlaceholderX(): number {
    if (isLandscapeLayout()) {
        return landscapeClusterMetrics().stockX
    }
    return sideColumnX()
}
export function stockPlaceholderY(): number {
    return isLandscapeLayout() ? landscapeTopRowY() : rowY(8)
}

// ─── Waste (Row 8) ──────────────────────────────────────────────────────

export function wasteCardX(): number {
    if (isLandscapeLayout()) {
        return landscapeClusterMetrics().wasteX
    }
    return mainColumnX()
}
export function wasteCardY(): number {
    return isLandscapeLayout() ? landscapeTopRowY() : rowY(8)
}
export function wastePlaceholderX(): number {
    if (isLandscapeLayout()) {
        return landscapeClusterMetrics().wasteX
    }
    return mainColumnX()
}
export function wastePlaceholderY(): number {
    return isLandscapeLayout() ? landscapeTopRowY() : rowY(8)
}

// ─── Tableau (Rows 1–7) ─────────────────────────────────────────────────

export function tableauCardX(_i: number): number {
    if (!isLandscapeLayout()) {
        return mainColumnX()
    }
    const { startX, gap } = landscapeTableauMetrics()
    const columnIndex = isLeftHanded() ? appState.NUM_TABLEAU - 1 - _i : _i
    return Math.floor(startX + columnIndex * (appState.cardWidth + gap))
}
export function tableauCardY(i: number): number {
    if (isLandscapeLayout()) {
        return landscapeTableauRowY()
    }
    // Tableau column i sits on grid row (i + 1)
    return rowY(i + 1)
}
export function tableauPlaceholderX(_i: number): number {
    if (!isLandscapeLayout()) {
        return mainColumnX()
    }
    const { startX, gap } = landscapeTableauMetrics()
    const columnIndex = isLeftHanded() ? appState.NUM_TABLEAU - 1 - _i : _i
    return Math.floor(startX + columnIndex * (appState.cardWidth + gap))
}
export function tableauPlaceholderY(i: number): number {
    if (isLandscapeLayout()) {
        return landscapeTableauRowY()
    }
    return rowY(i + 1)
}

/** Horizontal step for one card in a tableau cascade (face-down cards are much tighter). */
export function tableauCardStep(isFaceDown: boolean): number {
    if (isLandscapeLayout()) {
        return appState.cardHeight * (isFaceDown ? 0.12 : 0.28)
    }
    return appState.cardWidth * (isFaceDown ? 0.08 : 0.42)
}

/** Total horizontal extent of a tableau pile, excluding the last card's own width. */
export function tableauPileExtent(cards: Card[]): number {
    let offset = 0
    for (let i = 0; i < cards.length - 1; i++) {
        offset += tableauCardStep(cards[i].isFaceDown)
    }
    return offset
}

// ─── Foundation (Rows 3–6) ──────────────────────────────────────────────

export function foundationCardX(index: number = 0): number {
    if (isLandscapeLayout()) {
        const { foundationClusterX, gap } = landscapeClusterMetrics()
        return Math.floor(foundationClusterX + index * (appState.cardWidth + gap))
    }
    return sideColumnX()
}
export function foundationCardY(i: number): number {
    if (isLandscapeLayout()) {
        return landscapeTopRowY()
    }
    // Foundation i sits on grid row (i + 3)
    return rowY(i + 3)
}
export function foundationPlaceholderX(index: number = 0): number {
    if (isLandscapeLayout()) {
        const { foundationClusterX, gap } = landscapeClusterMetrics()
        return Math.floor(foundationClusterX + index * (appState.cardWidth + gap))
    }
    return sideColumnX()
}
export function foundationPlaceholderY(i: number): number {
    if (isLandscapeLayout()) {
        return landscapeTopRowY()
    }
    return rowY(i + 3)
}

// ─── Buttons ────────────────────────────────────────────────────────────

// Menu — Button row 0, side column
export function menuButtonX(): number {
    return sideColumnX()
}
export function menuButtonY(): number {
    return buttonRowY(0)
}

// New Game — Button row 1, side column
export function newGameButtonX(): number {
    return sideColumnX()
}
export function newGameButtonY(): number {
    return buttonRowY(1)
}

// Toggle records button — same position as New Game on completion screen.
export function recordsToggleButtonX(): number {
    return oppositeColumnX()
}

export function recordsToggleButtonY(): number {
    return buttonRowY(1)
}

// Undo — Button row 2, side column
export function undoButtonX(): number {
    return sideColumnX()
}
export function undoButtonY(): number {
    return buttonRowY(2)
}

// Autocomplete — bottom far side from the deck (portrait), button row 3 (landscape)
export function autoCompleteButtonX(): number {
    if (isLandscapeLayout()) return sideColumnX()
    return oppositeColumnX()
}
export function autoCompleteButtonY(): number {
    if (isLandscapeLayout()) return buttonRowY(3)
    return rowY(8) // same row as stock/waste — bottom of screen, opposite side from deck
}
